import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Navigation />
      <main className="flex-1 pt-24 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-500 text-sm mb-2">Effective Date: February 8, 2026</p>
          <h1 className="text-3xl font-bold text-white mb-10">Privacy Notice</h1>

          <div className="prose prose-invert prose-sm max-w-none space-y-8 text-gray-300 leading-relaxed">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. Data We Collect</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>
                  <strong className="text-gray-300">Identity Data:</strong> Name, email
                  address, username (via Google/GitHub OAuth).
                </li>
                <li>
                  <strong className="text-gray-300">Financial Data:</strong> Partial credit
                  card data (last 4 digits), billing address (processed via Stripe).
                </li>
                <li>
                  <strong className="text-gray-300">Technical Data:</strong> IP address,
                  browser type, device identifiers, login logs, and compute instance metadata.
                </li>
                <li>
                  <strong className="text-gray-300">Usage Data:</strong> Compute hours,
                  notebook execution logs, API calls.
                </li>
                <li>
                  <strong className="text-gray-300">User Content:</strong> Jupyter notebooks,
                  datasets uploaded, and algorithmic code.
                </li>
              </ul>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. How We Use Your Data</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>
                  <strong className="text-gray-300">Service Provision:</strong> To provision
                  cloud instances, authenticate access, and execute code.
                </li>
                <li>
                  <strong className="text-gray-300">Billing:</strong> To calculate metered
                  usage and process payments.
                </li>
                <li>
                  <strong className="text-gray-300">Security:</strong> To detect fraud, abuse
                  (e.g., crypto-mining), and platform vulnerabilities.
                </li>
              </ul>

              <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4 mt-4">
                <h3 className="text-sm font-semibold text-blue-300 mb-2">
                  Google API Limited Use Disclosure
                </h3>
                <p className="text-gray-400 text-sm">
                  Propalgos's use and transfer to any other app of information received from
                  Google APIs will adhere to the{' '}
                  <strong className="text-gray-300">
                    Google API Services User Data Policy
                  </strong>
                  , including the Limited Use requirements.
                </p>
                <ul className="list-disc list-inside space-y-1 mt-2 text-gray-400 text-sm">
                  <li>
                    We do not allow humans to read this data unless you have explicitly agreed
                    for specific messages/files, or for security/fraud investigations.
                  </li>
                  <li>We do not use Google User Data for advertising purposes.</li>
                  <li>We do not sell Google User Data to third parties.</li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. Subprocessors</h2>
              <p className="mb-4">
                We use the following third-party service providers to process data. By using
                the Platform, you consent to this processing.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="py-2 pr-4 text-gray-300 font-medium">Subprocessor</th>
                      <th className="py-2 pr-4 text-gray-300 font-medium">Purpose</th>
                      <th className="py-2 text-gray-300 font-medium">Location</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-400">
                    <tr className="border-b border-gray-800">
                      <td className="py-2 pr-4 font-medium text-gray-300">Amazon Web Services (AWS)</td>
                      <td className="py-2 pr-4">Cloud Infrastructure / Compute Hosting</td>
                      <td className="py-2">USA / Global</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 pr-4 font-medium text-gray-300">Google Cloud Platform (GCP)</td>
                      <td className="py-2 pr-4">Cloud Infrastructure / Compute Hosting</td>
                      <td className="py-2">USA / Global</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 pr-4 font-medium text-gray-300">Stripe</td>
                      <td className="py-2 pr-4">Payment Processing (PCI-DSS Compliant)</td>
                      <td className="py-2">USA</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 pr-4 font-medium text-gray-300">Auth0 / Google / GitHub</td>
                      <td className="py-2 pr-4">Authentication Services</td>
                      <td className="py-2">USA</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 pr-4 font-medium text-gray-300">PostHog</td>
                      <td className="py-2 pr-4">Product Analytics (Self-Hosted/EU Cloud)</td>
                      <td className="py-2">USA / EU</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 pr-4 font-medium text-gray-300">SendGrid / Twilio</td>
                      <td className="py-2 pr-4">Transactional Emails</td>
                      <td className="py-2">USA</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. International Transfers</h2>
              <p>
                For users in the EEA/UK, we transfer data to the US using Standard
                Contractual Clauses (SCCs) and/or reliance on the EU-U.S. Data Privacy
                Framework where applicable. We implement supplementary measures such as
                encryption in transit and at rest to protect your data during transfer.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. Your Rights (GDPR & CCPA)</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>
                  <strong className="text-gray-300">Right to Erasure (Art. 17 GDPR):</strong>{' '}
                  You may request deletion of your account and data via your Account Settings
                  or by emailing privacy@propalgos.ai.
                </li>
                <li>
                  <strong className="text-gray-300">Right to Portability (Art. 20 GDPR):</strong>{' '}
                  You may export your notebooks and usage history in a machine-readable format
                  (JSON/CSV).
                </li>
                <li>
                  <strong className="text-gray-300">Right to Object:</strong> You may opt-out
                  of marketing communications at any time.
                </li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. Data Retention</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>
                  <strong className="text-gray-300">Compute Logs:</strong> Retained for 90
                  days for security auditing, then anonymized.
                </li>
                <li>
                  <strong className="text-gray-300">Notebooks/Storage:</strong> Retained for
                  the life of the account unless deleted by the user.
                </li>
                <li>
                  <strong className="text-gray-300">Billing Records:</strong> Retained for 7
                  years for tax compliance.
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Contact</h2>
              <p>
                For privacy-related inquiries, contact us at{' '}
                <a
                  href="mailto:privacy@propalgos.ai"
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  privacy@propalgos.ai
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
